import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { MuiPickersUtilsProvider, DatePicker } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";

import { useNotify, useRefresh } from "react-admin";

import { logError, logHistory } from "../../../utils/appUtils";
import {
  InvoiceStatus,
  PaymentMethod,
  PaymentMethods,
  ResourceType,
} from "../../../utils/constants";
import { formatCurrency, trimDecimal } from "../../../utils/utils";

import {
  describePayment,
  getBalance,
  planRecordPayment,
  sumPayments,
} from "./invoicePaymentUtils";
import {
  CONFLICT_MESSAGE,
  readInvoicePaymentState,
  saveInvoiceChange,
} from "./saveInvoiceChange";

const SKIP_MESSAGES = {
  recorded: "This payment was already recorded.",
  notPending: "The invoice is no longer pending; the payment was not recorded.",
};

// Noon local time keeps the picked day the same in any US time zone.
const toPaymentDate = (date) => {
  const day = new Date(date);
  day.setHours(12, 0, 0, 0);
  return day.toISOString();
};

// The dialog renders in a portal, but React still bubbles its clicks to the
// list row behind the button; stop them there. Native events are left alone
// so the date picker and select keep working.
const stop = (e) => e.stopPropagation();

/**
 * Records one payment against a PENDING invoice. Reads the invoice fresh on
 * open, then saves through saveInvoiceChange, which writes only over the
 * invoice as just read, so a payment someone else saves at the same moment is
 * never overwritten. The payment id is fixed per opening: saving again after a
 * lost response finds the payment already there instead of adding it twice.
 */
export const RecordPaymentDialog = ({ open, invoiceId, onClose }) => {
  const notify = useNotify();
  const refresh = useRefresh();

  const [invoice, setInvoice] = useState(null);
  const [paymentId, setPaymentId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [amount, setAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(new Date());
  const [method, setMethod] = useState(PaymentMethod.CHECK);
  const [reference, setReference] = useState("");
  const [memo, setMemo] = useState("");

  useEffect(() => {
    if (!open || !invoiceId) return;
    let cancelled = false;

    setInvoice(null);
    setPaymentId(uuidv4());
    setAmount("");
    setPaymentDate(new Date());
    setMethod(PaymentMethod.CHECK);
    setReference("");
    setMemo("");
    setLoading(true);

    readInvoicePaymentState(invoiceId)
      .then((fresh) => {
        if (cancelled) return;
        if (fresh.status !== InvoiceStatus.PENDING) {
          notify(
            "Payments can only be recorded on pending invoices.",
            "warning",
          );
          onClose();
          return;
        }
        setInvoice(fresh);
        const balance = getBalance(fresh.total, sumPayments(fresh.payments));
        setAmount(balance > 0 ? balance.toFixed(2) : "");
      })
      .catch((error) => {
        if (cancelled) return;
        logError("invoices:RecordPaymentDialog:fetchInvoice", error, notify);
        onClose();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, invoiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const paid = sumPayments(invoice?.payments);
  const balance = invoice ? getBalance(invoice.total, paid) : 0;
  const amountValue = Number(amount);
  const amountValid =
    amount !== "" && Number.isFinite(amountValue) && amountValue > 0;
  const overBy = amountValid ? trimDecimal(amountValue - balance) : 0;
  const dateValid = paymentDate && !isNaN(new Date(paymentDate).getTime());

  const handleSave = async () => {
    if (!amountValid || !dateValid) return;
    setSaving(true);
    try {
      const payment = {
        id: paymentId,
        amount: trimDecimal(amountValue),
        paymentDate: toPaymentDate(paymentDate),
        method,
        reference: reference.trim() || null,
        memo: memo.trim() || null,
        recordedBy: window.appUser?.fullName || null,
        recordedAt: new Date().toISOString(),
      };
      // The plan runs on the invoice as read at save time: someone may have
      // recorded a payment or completed the invoice since the dialog opened.
      const result = await saveInvoiceChange(invoiceId, (fresh) =>
        planRecordPayment(fresh, payment),
      );

      if (result.skip === "conflict") {
        // Keep what was typed; show the invoice as it is now.
        notify(CONFLICT_MESSAGE, "warning");
        setInvoice(await readInvoicePaymentState(invoiceId));
        refresh();
        return;
      }
      if (result.skip) {
        notify(SKIP_MESSAGES[result.skip], "warning");
        onClose();
        refresh();
        return;
      }

      const { invoice: fresh, update } = result;
      const completed = update.status === InvoiceStatus.PAID;
      logHistory({
        text: `Payment ${describePayment(payment)} recorded.${
          completed ? " Invoice paid/completed." : ""
        }`,
        parentId: fresh.id,
        parentType: ResourceType.INVOICE,
      });

      notify(
        completed
          ? "Payment recorded. Invoice paid in full."
          : `Payment recorded. Balance ${formatCurrency(update.balance)}.`,
        "info",
      );
      onClose();
      refresh();
    } catch (error) {
      logError("invoices:RecordPaymentDialog:handleSave", error, notify);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) onClose();
  };

  const isCheck = method === PaymentMethod.CHECK;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      onClick={stop}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        Record Payment
        {invoice?.invoiceNumber ? ` — Invoice ${invoice.invoiceNumber}` : ""}
      </DialogTitle>
      <DialogContent>
        {loading || !invoice ? (
          <Grid container justifyContent="center">
            <CircularProgress size={32} />
          </Grid>
        ) : (
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  {invoice.accountName} · Total {formatCurrency(invoice.total)}
                  {paid > 0 ? ` · Paid ${formatCurrency(paid)}` : ""} · Balance{" "}
                  {formatCurrency(balance)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Amount"
                  variant="outlined"
                  fullWidth
                  required
                  autoFocus
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  error={amount !== "" && !amountValid}
                  helperText={
                    amount !== "" && !amountValid
                      ? "Enter an amount above zero."
                      : overBy > 0
                        ? `${formatCurrency(overBy)} over balance`
                        : " "
                  }
                  inputProps={{ min: 0, step: "0.01", inputMode: "decimal" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Payment Date"
                  inputVariant="outlined"
                  fullWidth
                  required
                  format="MM/dd/yyyy"
                  value={paymentDate}
                  onChange={setPaymentDate}
                  helperText=" "
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Method"
                  variant="outlined"
                  fullWidth
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                >
                  {PaymentMethods.map((choice) => (
                    <MenuItem key={choice.id} value={choice.id}>
                      {choice.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={isCheck ? "Check No." : "Reference"}
                  variant="outlined"
                  fullWidth
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Memo"
                  variant="outlined"
                  fullWidth
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  helperText="Internal, not printed on the invoice."
                />
              </Grid>
            </Grid>
          </MuiPickersUtilsProvider>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSave}
          disabled={!invoice || saving || !amountValid || !dateValid}
        >
          {saving ? <CircularProgress size={20} /> : "Record Payment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
